import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { showToast } from '../../../components/Toast';
import { apiDownload, apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { oggiRoma } from '../../../utils/dateRoma';
import { formatCurrency, formatNumber } from '../../../utils/formatters';
import { Kpi } from './componenti';
import { MOD_RIADDEBITI, Riaddebito, etichettaMese, formatDataOra, messaggioErrore, whInKwh } from './tipi';
import { API_DOCS } from './RiaddebitiRicaricaPage.api';

type Conferma = 'chiudi' | 'riapri' | null;

// Quanti mesi, a partire da quello corrente, propone il selettore
const MESI_PROPOSTI = 24;

// Mese nel formato AAAA-MM: confrontabile come stringa
function chiaveMese(anno: number, mese: number): string {
  return `${anno}-${String(mese).padStart(2, '0')}`;
}

function elencoMesi(oggi: string, quanti: number): string[] {
  let anno = Number(oggi.slice(0, 4));
  let mese = Number(oggi.slice(5, 7));
  const mesi: string[] = [];
  for (let i = 0; i < quanti; i++) {
    mesi.push(chiaveMese(anno, mese));
    mese -= 1;
    if (mese === 0) {
      mese = 12;
      anno -= 1;
    }
  }
  return mesi;
}

function conta(n: number, singolare: string, plurale: string): string {
  return `${n} ${n === 1 ? singolare : plurale}`;
}

function esclusi(anomale: number, inCorso: number): string {
  const parti: string[] = [];
  if (anomale > 0) parti.push(conta(anomale, 'ricarica anomala', 'ricariche anomale'));
  if (inCorso > 0) parti.push(conta(inCorso, 'ricarica in corso', 'ricariche in corso'));
  return parti.join(' e ');
}

// Stessa regola del backend (services/ricarica/export.js) per il nome del file
function slug(testo: string): string {
  const s = testo
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return s || 'societa';
}

export default function RiaddebitiRicaricaPage() {
  const { can, isSuperadmin } = usePermesso();
  const canEdit = can(MOD_RIADDEBITI, 'edit');
  const { societaId, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();

  const oggi = oggiRoma();
  const meseCorrente = oggi.slice(0, 7);

  const [mese, setMese] = useState(meseCorrente);
  const [dati, setDati] = useState<Riaddebito | null>(null);
  const [caricamento, setCaricamento] = useState(false);
  const [errore, setErrore] = useState('');
  const [conferma, setConferma] = useState<Conferma>(null);
  const [occupato, setOccupato] = useState(false);
  const [esportando, setEsportando] = useState(false);
  // Numero dell'ultima richiesta: una risposta arrivata in ritardo non sovrascrive quella attuale
  const ultimaRichiesta = useRef(0);

  const anno = Number(mese.slice(0, 4));
  const meseNum = Number(mese.slice(5, 7));

  const opzioniMesi = useMemo(() => {
    const mesi = elencoMesi(meseCorrente, MESI_PROPOSTI);
    return mesi.includes(mese) ? mesi : [...mesi, mese].sort().reverse();
  }, [meseCorrente, mese]);

  const carica = useCallback(async () => {
    const richiesta = ++ultimaRichiesta.current;
    if (!societaId) {
      setDati(null);
      setErrore('');
      setCaricamento(false);
      return;
    }
    setDati(null);
    setErrore('');
    setCaricamento(true);
    try {
      const p = new URLSearchParams({ societaId, anno: String(anno), mese: String(meseNum) });
      // @api GET /api/ricarica/riaddebiti?societaId&anno&mese -> {"success":"true","data":{"periodo":{"stato":"APERTO|CHIUSO","chiusoAt":"string|null","chiusoDa":"string|null"},"righe":[{"dipendente":{"id":"number","nome":"string","cognome":"string","codiceFiscale":"string|null"},"sessioni":"number","energiaWh":"number","importo":"number","tardive":"number"}],"totali":{"sessioni":"number","energiaWh":"number","importo":"number"},"avvisi":{"anomale":"number","inCorso":"number","nonCollegate":"number","precedentiNonChiusi":[{"anno":"number","mese":"number","sessioni":"number"}]}}}
      const res = await apiFetch<{ success: boolean; data: Riaddebito }>(`/api/ricarica/riaddebiti?${p}`);
      if (richiesta !== ultimaRichiesta.current) return;
      setDati(res.data);
    } catch (err) {
      if (richiesta !== ultimaRichiesta.current) return;
      setErrore(messaggioErrore(err, 'Errore nel caricamento dei riaddebiti'));
    } finally {
      if (richiesta === ultimaRichiesta.current) setCaricamento(false);
    }
  }, [societaId, anno, meseNum]);

  useEffect(() => { carica(); }, [carica]);

  const chiuso = dati?.periodo.stato === 'CHIUSO';
  const vuoto = !dati || dati.righe.length === 0;
  // Si chiude solo un mese finito (ora italiana): il mese corrente resta aperto
  const meseFinito = mese < meseCorrente;
  const nomeMese = etichettaMese(anno, meseNum).toLowerCase();
  const daEscludere = dati ? esclusi(dati.avvisi.anomale, dati.avvisi.inCorso) : '';

  const esporta = async () => {
    if (!dati || !societaId) return;
    setEsportando(true);
    try {
      const p = new URLSearchParams({ societaId, anno: String(anno), mese: String(meseNum) });
      const nomeSocieta = listaSocieta.find((s) => String(s.id) === societaId)?.nome ?? '';
      const nomeFile = `riaddebito-ricariche-${slug(nomeSocieta)}-${mese}${chiuso ? '' : '-provvisorio'}.xlsx`;
      // @api GET /api/ricarica/riaddebiti/export?societaId&anno&mese -> file xlsx (fogli Riepilogo e Dettaglio; "-provvisorio" nel nome se il mese non è chiuso)
      await apiDownload(`/api/ricarica/riaddebiti/export?${p}`, nomeFile);
    } catch (err) {
      showToast(messaggioErrore(err, "Errore nell'esportazione"), 'error');
    } finally {
      setEsportando(false);
    }
  };

  const eseguiConferma = async () => {
    if (!conferma || !societaId) return;
    const azione = conferma;
    setOccupato(true);
    try {
      const body = JSON.stringify({ societaId: Number(societaId), anno, mese: meseNum });
      if (azione === 'chiudi') {
        // @api POST /api/ricarica/riaddebiti/chiudi {"societaId":"number","anno":"number","mese":"number"} -> {"success":"true","data":{"sessioni":"number"}}
        const res = await apiFetch<{ success: boolean; data: { sessioni: number } }>('/api/ricarica/riaddebiti/chiudi', {
          method: 'POST',
          body,
        });
        showToast(`Mese chiuso: ${conta(res.data.sessioni, 'ricarica collegata', 'ricariche collegate')}`, 'success');
      } else {
        // @api POST /api/ricarica/riaddebiti/riapri {"societaId":"number","anno":"number","mese":"number"} -> {"success":"true","data":{"sessioni":"number"}}
        const res = await apiFetch<{ success: boolean; data: { sessioni: number } }>('/api/ricarica/riaddebiti/riapri', {
          method: 'POST',
          body,
        });
        showToast(`Mese riaperto: ${conta(res.data.sessioni, 'ricarica scollegata', 'ricariche scollegate')}`, 'success');
      }
    } catch (err) {
      showToast(
        messaggioErrore(err, azione === 'chiudi' ? 'Errore nella chiusura del mese' : 'Errore nella riapertura del mese'),
        'error',
      );
    } finally {
      setOccupato(false);
      setConferma(null);
    }
    // Anche dopo un errore (es. mese già chiuso da un altro utente) si rilegge lo stato vero
    carica();
  };

  const annullaConferma = () => {
    if (!occupato) setConferma(null);
  };

  return (
    <Layout>
      <div className="bop-ricarica__stack">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2 className="bop-page-title">Riaddebiti</h2>
            <p className="bop-page-subtitle">Ricariche dei dipendenti della società, da passare alle paghe</p>
          </div>
          <div className="bop-flex">
            <button className="bop-btn bop-btn-secondary" disabled={vuoto || esportando} onClick={esporta}
              title={chiuso ? undefined : 'Il file è provvisorio finché il mese non è chiuso'}>
              {esportando ? 'Esportazione…' : chiuso || vuoto ? 'Esporta Excel' : 'Esporta Excel (provvisorio)'}
            </button>
            {dati && !chiuso && !vuoto && canEdit && (
              <button className="bop-btn bop-btn-primary" disabled={!meseFinito}
                title={meseFinito ? undefined : 'Il mese si chiude quando è finito'}
                onClick={() => setConferma('chiudi')}>
                Chiudi il mese
              </button>
            )}
            {dati && chiuso && isSuperadmin && (
              <button className="bop-btn bop-btn-secondary" onClick={() => setConferma('riapri')}>Riapri il mese</button>
            )}
          </div>
        </div>

        <div className="bop-filter-bar">
          {!isUnica && (
            <select className="bop-select" aria-label="Società" value={societaId}
              onChange={(e) => setSocietaId(e.target.value)}>
              <option value="">Seleziona società…</option>
              {listaSocieta.map((s) => (<option key={s.id} value={s.id}>{s.nome}</option>))}
            </select>
          )}
          <select className="bop-select" aria-label="Mese" value={mese} onChange={(e) => setMese(e.target.value)}>
            {opzioniMesi.map((m) => (
              <option key={m} value={m}>{etichettaMese(Number(m.slice(0, 4)), Number(m.slice(5, 7)))}</option>
            ))}
          </select>
        </div>

        {!societaId ? (
          <div className="bop-card">
            <div className="bop-empty-state">
              <p className="bop-empty-state__text">Seleziona una società in alto per vedere i riaddebiti</p>
            </div>
          </div>
        ) : caricamento ? (
          <div className="bop-card">
            <div className="bop-empty-state">
              <p className="bop-empty-state__text">Caricamento…</p>
            </div>
          </div>
        ) : errore ? (
          <div className="bop-alert bop-alert--danger bop-mb-0">{errore}</div>
        ) : dati ? (
          <>
            <div className="bop-flex">
              {chiuso ? (
                <>
                  <span className="bop-badge bop-badge--neutral">Chiuso</span>
                  <span className="bop-ricarica__nota">
                    il {formatDataOra(dati.periodo.chiusoAt)} da {dati.periodo.chiusoDa}: i totali non cambiano più
                  </span>
                </>
              ) : (
                <>
                  <span className="bop-badge bop-badge--success">Aperto</span>
                  <span className="bop-ricarica__nota">
                    {meseFinito
                      ? 'i totali possono ancora cambiare finché non chiudi il mese'
                      : 'mese in corso: i totali possono ancora cambiare, si chiude quando è finito'}
                    {!vuoto && '. Il file Excel di un mese aperto è provvisorio e lo dice nel nome'}
                  </span>
                </>
              )}
            </div>

            {dati.avvisi.precedentiNonChiusi.length > 0 && (
              <div className="bop-alert bop-alert--warning bop-mb-0">
                <p className="bop-mb-0">
                  Ci sono ricariche concluse in mesi precedenti che non sono stati chiusi. Non arrivano alle paghe:
                  per mandarle si chiude quel mese e se ne esporta il file. Apri il mese per chiuderlo.
                </p>
                <div className="bop-ricarica__comandi bop-mt-2">
                  {dati.avvisi.precedentiNonChiusi.map((p) => (
                    <button key={chiaveMese(p.anno, p.mese)} type="button" className="bop-btn-link"
                      onClick={() => setMese(chiaveMese(p.anno, p.mese))}>
                      {etichettaMese(p.anno, p.mese)} ({conta(p.sessioni, 'ricarica', 'ricariche')})
                    </button>
                  ))}
                </div>
              </div>
            )}

            {dati.avvisi.nonCollegate > 0 && (
              <div className="bop-alert bop-alert--danger bop-mb-0">
                {conta(dati.avvisi.nonCollegate, 'ricarica di questo mese non risulta', 'ricariche di questo mese non risultano')}{' '}
                collegata alla chiusura e non {dati.avvisi.nonCollegate === 1 ? 'compare' : 'compaiono'} nei totali.
                Non dovrebbe succedere: segnalalo all'assistenza.
              </div>
            )}

            {daEscludere && (
              <div className="bop-alert bop-alert--warning bop-mb-0">
                Fuori dal riaddebito: {daEscludere}.
                {chiuso
                  ? ' Quelle confermate più avanti vanno nel primo mese aperto, come tardive.'
                  : ' Le anomale entrano dopo la conferma nella pagina Sessioni.'}
              </div>
            )}

            {vuoto ? (
              <div className="bop-card">
                <div className="bop-empty-state">
                  <h3 className="bop-empty-state__title">Nessuna ricarica</h3>
                  <p className="bop-empty-state__text">Nessuna ricarica da riaddebitare in {nomeMese}</p>
                </div>
              </div>
            ) : (
              <>
                <div className="bop-ricarica__kpi-grid">
                  <Kpi label="Dipendenti" valore={String(dati.righe.length)} />
                  <Kpi label="Ricariche" valore={String(dati.totali.sessioni)} />
                  <Kpi label="Energia" valore={`${formatNumber(whInKwh(dati.totali.energiaWh))} kWh`} />
                  <Kpi label="Da riaddebitare" valore={`${formatCurrency(dati.totali.importo)} €`} />
                </div>

                <div className="bop-card">
                  <div className="bop-table-wrapper">
                    <table className="bop-table">
                      <thead>
                        <tr>
                          <th>Dipendente</th>
                          <th>Codice fiscale</th>
                          <th className="bop-text-right">Ricariche</th>
                          <th className="bop-text-right">kWh</th>
                          <th className="bop-text-right">Importo</th>
                          <th>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dati.righe.map((r) => (
                          <tr key={r.dipendente.id}>
                            <td>{r.dipendente.cognome} {r.dipendente.nome}</td>
                            <td className="bop-text-mono">{r.dipendente.codiceFiscale ?? '—'}</td>
                            <td className="bop-text-right">{r.sessioni}</td>
                            <td className="bop-text-right">{formatNumber(whInKwh(r.energiaWh))}</td>
                            <td className="bop-text-right">{formatCurrency(r.importo)} €</td>
                            <td className="bop-text-muted">
                              {r.tardive > 0 ? `${conta(r.tardive, 'tardiva', 'tardive')} da un mese già chiuso` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bop-ricarica__totali">
                          <td>Totale</td>
                          <td />
                          <td className="bop-text-right">{dati.totali.sessioni}</td>
                          <td className="bop-text-right">{formatNumber(whInKwh(dati.totali.energiaWh))}</td>
                          <td className="bop-text-right">{formatCurrency(dati.totali.importo)} €</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        ) : null}

        {conferma && dati && (
          <div className="bop-modal-overlay" onClick={annullaConferma}>
            <div className="bop-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
              <div className="bop-modal__header">
                <h2 className="bop-modal__title">
                  {conferma === 'chiudi' ? `Chiudere ${nomeMese}?` : `Riaprire ${nomeMese}?`}
                </h2>
                <button className="bop-modal__close" onClick={annullaConferma} aria-label="Chiudi">✕</button>
              </div>
              <div className="bop-modal__body bop-ricarica__form">
                {conferma === 'chiudi' ? (
                  <>
                    <p className="bop-ricarica__nota">
                      Il mese viene congelato con {conta(dati.totali.sessioni, 'ricarica', 'ricariche')}: i totali non
                      cambiano più. Le ricariche che arrivano dopo (colonnine rimaste offline, anomale confermate)
                      finiscono nel primo mese aperto, segnate come tardive.
                    </p>
                    {daEscludere && (
                      <div className="bop-alert bop-alert--warning bop-mb-0">Restano fuori: {daEscludere}.</div>
                    )}
                  </>
                ) : (
                  <p className="bop-ricarica__nota">
                    Riaprire un mese è un'operazione riservata ai SUPERADMIN e resta nel registro. Se il riaddebito è già
                    passato alle paghe, i totali possono cambiare. Le ricariche tardive già spostate al mese successivo
                    restano dove sono.
                  </p>
                )}
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn bop-btn-secondary" disabled={occupato} onClick={annullaConferma}>Annulla</button>
                {conferma === 'chiudi' ? (
                  <button className="bop-btn bop-btn-primary" disabled={occupato} onClick={eseguiConferma}>
                    {occupato ? 'Chiusura…' : 'Chiudi il mese'}
                  </button>
                ) : (
                  <button className="bop-btn bop-btn-danger" disabled={occupato} onClick={eseguiConferma}>
                    {occupato ? 'Riapertura…' : 'Riapri il mese'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
